import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ADMIN_SESSION_KEY = "admin-authenticated";
const ADMIN_SESSION_TOKEN_KEY = "admin-session-token";

export function isAdminAuthenticated(): boolean {
  return sessionStorage.getItem(ADMIN_SESSION_KEY) === "true";
}

export function getAdminSessionToken(): string | null {
  return sessionStorage.getItem(ADMIN_SESSION_TOKEN_KEY);
}

export function clearAdminSession() {
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
  sessionStorage.removeItem(ADMIN_SESSION_TOKEN_KEY);
}

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-admin", {
        body: { password },
      });
      if (error) throw error;
      if (data?.valid) {
        sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
        if (data?.token) sessionStorage.setItem(ADMIN_SESSION_TOKEN_KEY, data.token);

        // Redeem the Clerk sign-in ticket so the user lands signed in as
        // admin@timeminers.app. Without this, supabase-js queries from
        // admin pages run as anon and Supabase RLS admin_select policies
        // filter every result to zero rows.
        if (data?.clerk_ticket && signInLoaded && signIn) {
          try {
            const result = await signIn.create({ strategy: "ticket", ticket: data.clerk_ticket });
            if (result.status === "complete" && result.createdSessionId) {
              await setActive({ session: result.createdSessionId });
            }
          } catch (err) {
            console.warn("[AdminLogin] ticket redeem failed:", err);
            // Soft-fail: legacy sessionStorage flag still grants AdminRoute access,
            // but RLS-gated data will be empty until a Clerk path sign-in.
            toast({
              title: "Signed in (limited)",
              description: "Couldn't auto-link Clerk session — list data may show empty.",
            });
          }
        }
        navigate("/admin");
      } else {
        toast({ title: "Access denied", description: "Incorrect password.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Could not verify password.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Verifying..." : "Sign In"}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-border text-center text-xs text-muted-foreground space-y-2">
            <p>or, if you have an admin account:</p>
            <a href="/sign-in" className="block text-sm font-medium text-primary hover:underline">Sign in with your Time Miners account →</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
